// Pricing model — single source of truth shared by the API (entitlement gating, Stripe
// wiring) and the dashboard (pricing UI). One plan: the **Keep Pass**.
//   - $49 / year. Grants KEEP_CREDITS_PER_YEAR new Keeps each year.
//   - Unused Keep credits roll over while the pass stays active (they're never reset — each
//     renewal simply adds more; creating a Keep spends one).
//   - Every Keep is unlimited: guests, original-quality photos, video, forever access. There
//     are no per-Keep tiers, base costs, add-ons, storage caps, or photo caps.
import type { AccountTier, AccountTierDef, KeepBranding, KeepSettings } from "./dto";

/** $49 / year. */
export const KEEP_PASS_PRICE_CENTS = 4900;
/** New Keeps granted per paid year (initial purchase + each renewal). Unused ones roll over. */
export const KEEP_CREDITS_PER_YEAR = 4;

/** One-time "extra Keep" add-on packs — bought à la carte when a host is out of credits.
 * Purchased Keeps roll over and never expire, exactly like pass Keeps. Priced à la carte with
 * a volume discount on the larger pack. */
export interface KeepPack {
  id: string;
  /** Keeps granted by this pack. */
  keeps: number;
  /** One-time price in cents. */
  priceCents: number;
  label: string;
  /** Per-Keep unit-price note (shown on the larger pack), or null. */
  per: string | null;
  /** Marketing badge, e.g. "Best value", or null. */
  tag: string | null;
}
export const KEEP_PACKS: KeepPack[] = [
  { id: "one", keeps: 1, priceCents: 1900, label: "1 extra Keep", per: null, tag: null },
  { id: "three", keeps: 3, priceCents: 4500, label: "3 extra Keeps", per: "$15 each", tag: "Best value" },
];
export function keepPackById(id: string | null | undefined): KeepPack | undefined {
  return KEEP_PACKS.find((p) => p.id === id);
}

export const ACCOUNT_TIERS: Record<AccountTier, AccountTierDef> = {
  free: {
    id: "free",
    label: "Free",
    priceCentsYear: 0,
    stripePriceEnvVar: null,
    blurb: "Set up a Keep and look around — start a pass when you're ready to share it.",
    features: ["Create & preview a Keep", "Publish with a Keep Pass"],
  },
  keeppass: {
    id: "keeppass",
    label: "Keep Pass",
    priceCentsYear: KEEP_PASS_PRICE_CENTS,
    stripePriceEnvVar: "STRIPE_PRICE_KEEPPASS",
    blurb: "One pass, every gathering — four new Keeps a year, rolled over if you don't use them.",
    features: [
      `${KEEP_CREDITS_PER_YEAR} new Keeps every year`,
      "Unused Keeps roll over",
      "Unlimited guests, photos & video",
      "Forever access & anytime download",
      "Face matching, AI moments & search",
    ],
  },
};

export const ACCOUNT_TIER_ORDER: AccountTier[] = ["free", "keeppass"];

export function accountTierFor(t: string | null | undefined): AccountTierDef {
  return ACCOUNT_TIERS[(t as AccountTier) ?? "free"] ?? ACCOUNT_TIERS.free;
}

/** Whether a tier is an active Keep Pass (i.e. unlocks publishing + unlimited Keeps). */
export function hasActivePass(tier: string | null | undefined): boolean {
  return tier === "keeppass";
}

/** Fallback branding for any field a Keep leaves unset — the shipped default theme. */
export const DEFAULT_BRANDING: Required<Omit<KeepBranding, "copy">> & {
  theme: Required<NonNullable<KeepBranding["theme"]>>;
  copy: NonNullable<KeepBranding["copy"]>;
} = {
  displayName: "Your Event",
  tagline: "",
  eventDate: "",
  eventDateNumeric: "",
  theme: {
    // Legacy default — the original bougainvillea look for Keeps created before the theme
    // system (no `preset`). New Keeps override all of this from their chosen THEME_PRESET.
    preset: "",
    motif: "floral",
    dark: false,
    accent: "#cf4a8e",
    accentDeep: "#a8316f",
    accentSoft: "#f6dce9",
    page: "#efe7df",
    paper: "#fbf7f2",
    card: "#ffffff",
    ink: "#3f5d72",
    inkSoft: "#7d96a6",
    line: "rgba(63,93,114,0.15)",
    muted: "#5e7484",
    scriptFont: "Pinyon Script",
    density: "lush",
    momentsLayout: "rows",
  },
  copy: {
    successNames: [],
    suggestedSearches: [],
    logoUrl: undefined,
  },
};

/** Default per-event settings: auto-approve on, no watermark, face-match on, uploads open. */
export const DEFAULT_SETTINGS: Required<KeepSettings> = {
  autoApprove: true,
  watermark: false,
  faceMatch: true,
  acceptingUploads: true,
};

export function resolveSettings(s: KeepSettings | null | undefined): Required<KeepSettings> {
  return { ...DEFAULT_SETTINGS, ...(s ?? {}) };
}

/** The text stamped onto downloaded photos when the watermark is on: `"{name} · {date}"`
 * (e.g. `Taner & Abbie · 06.20.26`). Date prefers the numeric branding date, compacted to
 * MM.DD.YY; falls back to the human date text, then to just the name. Shared so the dashboard
 * settings copy and the guest app's actual stamp always match. */
export function watermarkStamp(b: KeepBranding | null | undefined): string {
  const name = (b?.displayName || "").trim() || DEFAULT_BRANDING.displayName;
  const src = (b?.eventDateNumeric || b?.eventDate || "").trim();
  const nums = src.match(/\d+/g);
  const date = nums && nums.length >= 3 ? `${nums[0]}.${nums[1]}.${nums[2]!.slice(-2)}` : src;
  return date ? `${name} · ${date}` : name;
}

/** Deep-merge an event's (partial) branding over the defaults so the UI always has every field. */
export function resolveBranding(b: KeepBranding | null | undefined): typeof DEFAULT_BRANDING {
  const src = b ?? {};
  return {
    displayName: src.displayName || DEFAULT_BRANDING.displayName,
    tagline: src.tagline ?? DEFAULT_BRANDING.tagline,
    eventDate: src.eventDate ?? DEFAULT_BRANDING.eventDate,
    eventDateNumeric: src.eventDateNumeric ?? DEFAULT_BRANDING.eventDateNumeric,
    theme: { ...DEFAULT_BRANDING.theme, ...(src.theme ?? {}) },
    copy: { ...DEFAULT_BRANDING.copy, ...(src.copy ?? {}) },
  };
}
